import React, { useCallback, useMemo } from "react";
import { Checkbox, Flex, AgGridReact, AgGrid } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";

export const PAYMENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "DISTRIBUTION", label: "Distribution" },
  { value: "NEGATIVE_CAPITAL_CALL", label: "Negative capital call" },
  { value: "SUBTRACTION_CAPITAL_ACTIVITY", label: "Capital out" },
  { value: "NEGATIVE_CONTRIBUTION", label: "Negative contribution" },
];

export type PaymentTypeFilterModel = string[] | null;

/**
 * Checkbox-based filter for payment types.
 * State is derived from `filterParams.model` so that external resets
 * (e.g. AG Grid filter restore) are reflected automatically.
 */
export const PaymentTypeFilter = (filterParams: AgGridReact.CustomFilterProps) => {
  const { onModelChange, getValue } = filterParams;
  const selectedTypes: string[] = useMemo(
    () => (filterParams.model as string[] | null) ?? [],
    [filterParams.model],
  );

  const toggleType = useCallback(
    (value: string) => {
      const exists = selectedTypes.includes(value);
      const next = exists
        ? selectedTypes.filter((type) => type !== value)
        : [...selectedTypes, value];
      // null means "no filter active" (show all)
      onModelChange(next.length > 0 ? next : null);
    },
    [onModelChange, selectedTypes],
  );

  const doesFilterPass = useCallback(
    (params: AgGrid.IDoesFilterPassParams) => {
      if (!selectedTypes.length) {
        return true;
      }

      const nodeValue = getValue(params.node) as string | undefined;

      if (!nodeValue) {
        return false;
      }

      // Compare against labels (what getValue returns from the rendered column)
      const selectedLabels = PAYMENT_TYPE_OPTIONS.filter((opt) =>
        selectedTypes.includes(opt.value),
      ).map((opt) => opt.label);

      return selectedLabels.some((label) => nodeValue.startsWith(label));
    },
    [getValue, selectedTypes],
  );

  AgGridReact.useGridFilter({ doesFilterPass });

  return (
    <Flex
      data-testid="payment-type-filter"
      flexDirection="column"
      gap={Sizes.XS}
      alignItems="flex-start"
      justifyContent="flex-start"
    >
      {PAYMENT_TYPE_OPTIONS.map((option) => (
        <Checkbox
          key={option.value}
          label={option.label}
          checked={selectedTypes.includes(option.value)}
          onChange={() => toggleType(option.value)}
        />
      ))}
    </Flex>
  );
};
